import { Grid, Stack, Typography } from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useMFAttachedDocumentsContext } from '~/microfrontend/hooks/useMFModuleNameContext';
import { DocumentPreview } from '~/widgets/components/DocumentPreview';
import EmptyDocumentsIcon from '~assets/images/empty/emptyDocuments.svg?react';
import { FileDropzone } from '~components/FileDropzone';
import { EmptyContent } from '~components/reuseable/EmptyContent';
import { AttachedFilesStore } from '~modules/attachedFiles/AttachedFilesStore';
import { UploadedFileModel } from '~modules/attachedFiles/models/UploadedFileModel';
import { FileTypesStore } from '~modules/fileTypes/FileTypesStore';
import { DocumentFixedTypeModel } from '~modules/fileTypes/models/DocumentFixedTypeModel';

export const AttachedDocumentsWidget = observer(() => {
  const fileTypesStore = useLocalObservable(() => new FileTypesStore());
  const attachedFilesStore = useLocalObservable(() => new AttachedFilesStore());

  const {
    documentId,
    documentTitle,
    documentTypeId,
    mode,
    readonly,
    includeNonGroupType,
    withoutFixedTypes,
    notShowFixTypeLabels,
    title,
  } = useMFAttachedDocumentsContext();

  const isExistRequiredFixedTypes = useMemo(() => {
    return !!fileTypesStore.documentFixedTypes.find((fixedType) => fixedType.isRequired && fixedType.isEnabled);
  }, [fileTypesStore.documentFixedTypes]);

  const { t } = useTranslation('common');

  //Effects

  useEffect(() => {
    attachedFilesStore.getAttachedDocuments(mode, documentTypeId, documentId, documentTitle);

    if (!readonly) {
      fileTypesStore.getFileFormats(mode);
    }

    if (!withoutFixedTypes) {
      fileTypesStore.getDocumentFixedTypes(documentTypeId, documentId, mode, includeNonGroupType);
    }

    return () => {
      fileTypesStore.reset();
      attachedFilesStore.reset();
    };
  }, []);

  // Handlers

  const handleRefreshFixedTypesList = () => {
    if (withoutFixedTypes) {
      return;
    }

    fileTypesStore.getDocumentFixedTypes(documentTypeId, documentId, mode, includeNonGroupType);
  };

  const handleDeleteFile = (file: UploadedFileModel) => {
    attachedFilesStore.deleteUploadedFile(file, handleRefreshFixedTypesList);
  };

  // Render

  const renderDropZone = (documentFixedType?: DocumentFixedTypeModel) => {
    const disabled = documentFixedType && !documentFixedType.isEnabled;

    // Статус readonly в основном определяется из родительского проекта,
    // но для документов Эксплуатации подменных а/м этот статус определяется из documentFixedType
    const currentReadonlyStatus = documentFixedType?.isReadOnly || readonly;

    if (disabled || currentReadonlyStatus) {
      return;
    }

    const isSingleDropzone = fileTypesStore.documentFixedTypes.filter(({ isEnabled }) => isEnabled).length === 1;

    return (
      <Grid key={documentFixedType?.fixedType?.id} item xs={isSingleDropzone ? 12 : 6}>
        <FileDropzone
          attachedFilesStore={attachedFilesStore}
          maxFiles={documentFixedType?.isMulti ? 20 : 1}
          documentId={documentId}
          documentTitle={documentTitle}
          documentTypeId={documentTypeId}
          fixedType={documentFixedType?.fixedType}
          fileMode={mode}
          notShowFixTypeLabels={notShowFixTypeLabels}
          onUploadFiles={handleRefreshFixedTypesList}
          required={documentFixedType?.isRequired}
          fileFormats={fileTypesStore.fileFormats}
        />
      </Grid>
    );
  };

  const renderIsRequiredMessage = () => {
    if (!isExistRequiredFixedTypes || readonly) {
      return;
    }

    return (
      <Typography variant="caption" color="error.main">
        *{t('requiredDocuments')}
      </Typography>
    );
  };

  const renderDocuments = () => {
    if (attachedFilesStore.uploadedFilesLoaded && !attachedFilesStore.uploadedFiles.length) {
      return (
        <Grid item xs={12}>
          <EmptyContent IconComponent={<EmptyDocumentsIcon />} title={t('empty.documents.title')} />
        </Grid>
      );
    }

    return attachedFilesStore.uploadedFiles?.map((file) => (
      <DocumentPreview key={file.fileId} file={file} onDelete={handleDeleteFile} readonly={readonly} />
    ));
  };

  return (
    <Stack direction="column" gap={1} justifyContent="space-between" sx={{ height: '100%' }}>
      {title}
      {renderDocuments()}
      <Grid container spacing={2}>
        {fileTypesStore.documentFixedTypes.map(renderDropZone)}
      </Grid>
      {renderIsRequiredMessage()}
    </Stack>
  );
});
